import React, { useState, useEffect } from 'react';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Grid,
    Chip,
    Alert,
    Divider,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    IconButton,
    Tooltip,
    FormControlLabel,
    Switch as MuiSwitch,
    InputAdornment
} from '@mui/material';
import { styled } from '@mui/system';
import { LoadingButton } from '@mui/lab';
import {
    LogOut,
    Home,
    Calendar,
    DollarSign,
    Edit,
    AlertCircle,
    CheckCircle,
    XCircle,
    ArrowRightLeft,
    TrendingUp,
    FileText,
    Clock,
    Building,
    X as CloseIcon,
    Info,
    Trash2,
    Plus,
    Shield,
    RotateCcw
} from 'lucide-react';
import { showSnackbar } from '../../../../components/SnackBar/SnackBar';
import leaseManagementApi from '../../../../services/api/leaseManagement';
import { propertiesApi, tenantsApi } from '../../../../services/api';

const themeColor = "#2C5283";

// ==================== STYLED COMPONENTS ====================

const StyledCard = styled(Card)(({ theme }) => ({
    marginBottom: theme.spacing(2),
    border: "1px solid #DDE6ED",
    borderRadius: '12px',
    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
}));

const StyledDialog = styled(Dialog)(({ theme }) => ({
    '& .MuiDialog-paper': {
        borderRadius: '12px',
        minWidth: '500px',
        maxWidth: '700px',
        [theme.breakpoints.down('sm')]: {
            margin: '16px',
            width: 'calc(100% - 32px)',
            maxWidth: 'none',
            minWidth: 'unset'
        }
    },
}));

const DialogTitleStyled = styled(DialogTitle)({
    color: themeColor,
    fontWeight: 600,
    fontSize: '1.3rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderBottom: '1px solid #e0e0e0',
    backgroundColor: '#f8f9fa',
    padding: '20px 24px',
});

const ActionButton = styled(Button)(({ variant: buttonVariant }) => ({
    borderRadius: '8px',
    textTransform: 'none',
    fontWeight: 500,
    padding: '8px 20px',
    ...(buttonVariant === 'danger' && {
        backgroundColor: '#d32f2f',
        color: 'white',
        '&:hover': {
            backgroundColor: '#b71c1c',
        },
    }),
}));

// Enhanced Styled Components for Deposit Details
const DepositCardHeader = styled(Box)({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '20px',
    paddingBottom: '12px',
    borderBottom: `2px solid #e8f0f9`,
});

const DepositItemCard = styled(Card)(({ theme }) => ({
    marginBottom: '12px',
    backgroundColor: '#f8fbfe',
    border: '1px solid #d4e6f5',
    borderRadius: '10px',
    transition: 'all 0.3s ease',
    '&:hover': {
        boxShadow: '0 4px 12px rgba(44, 82, 131, 0.12)',
        transform: 'translateY(-2px)',
        borderColor: '#a0c4e8',
    },
    '& .MuiCardContent-root': {
        padding: '12px 16px',
        '&:last-child': {
            paddingBottom: '12px',
        }
    }
}));

const DepositAmountText = styled(Typography)({
    fontWeight: '700',
    fontSize: '1.1rem',
    color: themeColor,
    letterSpacing: '0.5px',
});

const RefundableChip = styled(Chip)({
    fontWeight: '600',
    minWidth: '110px',
    backgroundColor: '#d4edda !important',
    color: '#155724 !important',
    borderColor: '#c3e6cb !important',
});

const NonRefundableChip = styled(Chip)({
    fontWeight: '600',
    minWidth: '110px',
    backgroundColor: '#f8d7da !important',
    color: '#721c24 !important',
    borderColor: '#f5c6cb !important',
});

const DepositSummaryBox = styled(Box)(({ theme }) => ({
    marginTop: '20px',
    padding: '16px',
    backgroundColor: 'linear-gradient(135deg, #f0f7ff 0%, #e8f0f9 100%)',
    borderRadius: '10px',
    border: `2px solid #c5d9ed`,
    boxShadow: '0 2px 8px rgba(44, 82, 131, 0.08)',
}));

const DepositHeaderIcon = styled(Box)({
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
});

const InfoRow = ({ label, value, icon: Icon, valueColor }) => (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {Icon && <Icon size={18} color="#666" />}
            <Typography variant="body2" color="textSecondary">
                {label}
            </Typography>
        </Box>
        <Typography variant="body1" sx={{ fontWeight: 500, color: valueColor || 'inherit' }}>
            {value}
        </Typography>
    </Box>
);

// ==================== MAIN COMPONENT ====================

const LeaseManagementPanel = ({ lease, landlordId, onLeaseUpdated }) => {
    // State
    const [loading, setLoading] = useState(false);
    const [leaseDetails, setLeaseDetails] = useState(null);
    const [leaseHistory, setLeaseHistory] = useState(null);
    const [properties, setProperties] = useState([]);
    const [availableUnits, setAvailableUnits] = useState([]);

    // Dialog states
    const [showTerminateDialog, setShowTerminateDialog] = useState(false);
    const [showSwitchRoomDialog, setShowSwitchRoomDialog] = useState(false);
    const [showExtendDialog, setShowExtendDialog] = useState(false);
    const [showUpdateTermsDialog, setShowUpdateTermsDialog] = useState(false);
    const [showHistoryDialog, setShowHistoryDialog] = useState(false);
    const [showDepositDialog, setShowDepositDialog] = useState(false);

    // Form data
    const [terminateForm, setTerminateForm] = useState({
        terminationReason: '',
        vacateDate: '',
        refundDeposit: false
    });

    const [switchRoomForm, setSwitchRoomForm] = useState({
        newPropertyId: '',
        newHouseNumber: '',
        newMonthlyRent: '',
        switchDate: '',
        transferArrears: false,
        billingEnabled: true
    });

    const [extendForm, setExtendForm] = useState({
        newEndDate: '',
        newMonthlyRent: '',
        reason: ''
    });

    const [updateTermsForm, setUpdateTermsForm] = useState({
        monthlyRent: '',
        depositHeld: '',
        billingEnabled: null,
        waterUnits: '',
        electricityUnits: '',
        leaseStartDate: '',
        leaseEndDate: ''
    });

    const [depositForm, setDepositForm] = useState([]);

    // Load lease details on mount
    useEffect(() => {
        if (lease?.lease_id) {
            loadLeaseDetails();
        }
    }, [lease?.lease_id]);

    // Load properties for room switch
    useEffect(() => {
        if (landlordId) {
            loadProperties();
        }
    }, [landlordId]);

    // Load available units when property is selected
    useEffect(() => {
        if (switchRoomForm.newPropertyId) {
            loadAvailableUnits(switchRoomForm.newPropertyId);
        }
    }, [switchRoomForm.newPropertyId]);

    // ==================== DATA LOADING ====================

    const loadLeaseDetails = async () => {
        try {
            setLoading(true);
            const response = await leaseManagementApi.getLeaseDetails({
                lease_id: lease.lease_id,
                landlord_id: landlordId
            });

            if (response.ok) {
                setLeaseDetails(response.data);
            } else {
                showSnackbar({
                    title: "Error!",
                    message: response.data?.Error || "Failed to load lease details.",
                    type: "error",
                    position: { vertical: "bottom", horizontal: "right" },
                });
            }
        } catch (error) {
            console.error('Error loading lease details:', error);
        } finally {
            setLoading(false);
        }
    };

    const loadLeaseHistory = async () => {
        try {
            setLoading(true);
            const response = await leaseManagementApi.getLeaseHistory({
                tenant_id: lease.tenant_id,
                landlord_id: landlordId
            });

            if (response.ok) {
                setLeaseHistory(response.data);
                setShowHistoryDialog(true);
            } else {
                showSnackbar({
                    title: "Error!",
                    message: response.data?.Error || "Failed to load lease history.",
                    type: "error",
                    position: { vertical: "bottom", horizontal: "right" },
                });
            }
        } catch (error) {
            console.error('Error loading lease history:', error);
        } finally {
            setLoading(false);
        }
    };

    const loadProperties = async () => {
        try {
            const response = await propertiesApi.getAll(landlordId);
            if (response.ok) {
                setProperties(response.data || []);
            }
        } catch (error) {
            console.error('Error loading properties:', error);
        }
    };

    const loadAvailableUnits = async (propertyId) => {
        try {
            // Load all units for selected property
            const response = await propertiesApi.getUnits(propertyId);
            console.log('Available units response:', response);
            if (response.ok) {
                // Filter for vacant units only
                const allUnits = Array.isArray(response.data) ? response.data : (response.data?.units || []);
                console.log('All units:', allUnits);
                const vacantUnits = allUnits.filter(unit => unit.state === 'VACANT');
                console.log('Vacant units:', vacantUnits);
                setAvailableUnits(vacantUnits);
            }
        } catch (error) {
            console.error('Error loading available units:', error);
            setAvailableUnits([]);
        }
    };

    // ==================== LEASE OPERATIONS ====================

    const handleTerminateLease = async () => {
        if (!terminateForm.terminationReason || !terminateForm.vacateDate) {
            showSnackbar({
                title: "Validation Error!",
                message: "Please fill in all required fields.",
                type: "error",
                position: { vertical: "bottom", horizontal: "right" },
            });
            return;
        }

        if (!window.confirm('Are you sure you want to terminate this lease? This action cannot be undone.')) {
            return;
        }

        try {
            setLoading(true);
            const response = await leaseManagementApi.terminateLease({
                lease_id: lease.lease_id,
                landlord_id: landlordId,
                termination_reason: terminateForm.terminationReason,
                vacate_date: new Date(terminateForm.vacateDate).toISOString(),
                refund_deposit: terminateForm.refundDeposit
            });

            if (response.ok) {
                showSnackbar({
                    title: "Success!",
                    message: response.data.message || "Lease terminated successfully!",
                    type: "success",
                    position: { vertical: "bottom", horizontal: "right" },
                });

                // Show deposit refund details
                if (terminateForm.refundDeposit && response.data.refund_status) {
                    const refundMessage = `
Deposit Refund Details:
• Deposit Held: KES ${response.data.deposit_held?.toLocaleString()}
• Outstanding Balance: KES ${response.data.outstanding_balance?.toLocaleString()}
• Amount Refunded: KES ${response.data.deposit_refunded?.toLocaleString()}
• Status: ${response.data.refund_status}
                    `.trim();

                    setTimeout(() => {
                        alert(refundMessage);
                    }, 500);
                }

                handleCloseTerminateDialog();
                onLeaseUpdated?.();
            } else {
                showSnackbar({
                    title: "Error!",
                    message: response.data?.Error || "Failed to terminate lease.",
                    type: "error",
                    position: { vertical: "bottom", horizontal: "right" },
                });
            }
        } catch (error) {
            console.error('Error terminating lease:', error);
            showSnackbar({
                title: "Error!",
                message: "Failed to terminate lease. Please try again.",
                type: "error",
                position: { vertical: "bottom", horizontal: "right" },
            });
        } finally {
            setLoading(false);
        }
    };

    const handleSwitchRoom = async () => {
        if (!switchRoomForm.newPropertyId || !switchRoomForm.newHouseNumber ||
            !switchRoomForm.newMonthlyRent || !switchRoomForm.switchDate) {
            showSnackbar({
                title: "Validation Error!",
                message: "Please fill in all required fields.",
                type: "error",
                position: { vertical: "bottom", horizontal: "right" },
            });
            return;
        }

        if (!window.confirm('Are you sure you want to switch this tenant to a new room?')) {
            return;
        }

        try {
            setLoading(true);
            const response = await leaseManagementApi.switchRoom({
                lease_id: lease.lease_id,
                landlord_id: landlordId,
                new_property_id: parseInt(switchRoomForm.newPropertyId),
                new_house_number: switchRoomForm.newHouseNumber,
                new_monthly_rent: parseFloat(switchRoomForm.newMonthlyRent),
                switch_date: new Date(switchRoomForm.switchDate).toISOString(),
                transfer_arrears: switchRoomForm.transferArrears,
                billing_enabled: switchRoomForm.billingEnabled
            });

            if (response.ok) {
                showSnackbar({
                    title: "Success!",
                    message: response.data.message || "Room switched successfully!",
                    type: "success",
                    position: { vertical: "bottom", horizontal: "right" },
                });

                handleCloseSwitchRoomDialog();
                onLeaseUpdated?.();
            } else {
                showSnackbar({
                    title: "Error!",
                    message: response.data?.Error || "Failed to switch room.",
                    type: "error",
                    position: { vertical: "bottom", horizontal: "right" },
                });
            }
        } catch (error) {
            console.error('Error switching room:', error);
            showSnackbar({
                title: "Error!",
                message: "Failed to switch room. Please try again.",
                type: "error",
                position: { vertical: "bottom", horizontal: "right" },
            });
        } finally {
            setLoading(false);
        }
    };

    const handleExtendLease = async () => {
        if (!extendForm.newEndDate) {
            showSnackbar({
                title: "Validation Error!",
                message: "Please select a new end date.",
                type: "error",
                position: { vertical: "bottom", horizontal: "right" },
            });
            return;
        }

        try {
            setLoading(true);
            const payload = {
                lease_id: lease.lease_id,
                landlord_id: landlordId,
                new_end_date: new Date(extendForm.newEndDate).toISOString()
            };

            if (extendForm.newMonthlyRent) {
                payload.new_monthly_rent = parseFloat(extendForm.newMonthlyRent);
            }

            if (extendForm.reason) {
                payload.reason = extendForm.reason;
            }

            const response = await leaseManagementApi.extendLease(payload);

            if (response.ok) {
                showSnackbar({
                    title: "Success!",
                    message: response.data.message || "Lease extended successfully!",
                    type: "success",
                    position: { vertical: "bottom", horizontal: "right" },
                });

                handleCloseExtendDialog();
                onLeaseUpdated?.();
                loadLeaseDetails();
            } else {
                showSnackbar({
                    title: "Error!",
                    message: response.data?.Error || "Failed to extend lease.",
                    type: "error",
                    position: { vertical: "bottom", horizontal: "right" },
                });
            }
        } catch (error) {
            console.error('Error extending lease:', error);
            showSnackbar({
                title: "Error!",
                message: "Failed to extend lease. Please try again.",
                type: "error",
                position: { vertical: "bottom", horizontal: "right" },
            });
        } finally {
            setLoading(false);
        }
    };

    const handleUpdateTerms = async () => {
        const payload = {
            lease_id: lease.lease_id,
            landlord_id: landlordId
        };

        // Only add non-empty fields
        if (updateTermsForm.monthlyRent) payload.monthly_rent = parseFloat(updateTermsForm.monthlyRent);
        if (updateTermsForm.depositHeld) payload.deposit_held = parseFloat(updateTermsForm.depositHeld);
        if (updateTermsForm.billingEnabled !== null) payload.billing_enabled = updateTermsForm.billingEnabled;
        if (updateTermsForm.waterUnits) payload.water_units = parseInt(updateTermsForm.waterUnits);
        if (updateTermsForm.electricityUnits) payload.electricity_units = parseInt(updateTermsForm.electricityUnits);
        if (updateTermsForm.leaseStartDate) payload.lease_start_date = new Date(updateTermsForm.leaseStartDate).toISOString();
        if (updateTermsForm.leaseEndDate) payload.lease_end_date = new Date(updateTermsForm.leaseEndDate).toISOString();

        // Check if any field was updated
        if (Object.keys(payload).length <= 2) {
            showSnackbar({
                title: "Validation Error!",
                message: "Please update at least one field.",
                type: "error",
                position: { vertical: "bottom", horizontal: "right" },
            });
            return;
        }

        try {
            setLoading(true);
            const response = await leaseManagementApi.updateLeaseTerms(payload);

            if (response.ok) {
                showSnackbar({
                    title: "Success!",
                    message: response.data.message || "Lease terms updated successfully!",
                    type: "success",
                    position: { vertical: "bottom", horizontal: "right" },
                });

                handleCloseUpdateTermsDialog();
                onLeaseUpdated?.();
                loadLeaseDetails();
            } else {
                showSnackbar({
                    title: "Error!",
                    message: response.data?.Error || "Failed to update lease terms.",
                    type: "error",
                    position: { vertical: "bottom", horizontal: "right" },
                });
            }
        } catch (error) {
            console.error('Error updating lease terms:', error);
            showSnackbar({
                title: "Error!",
                message: "Failed to update lease terms. Please try again.",
                type: "error",
                position: { vertical: "bottom", horizontal: "right" },
            });
        } finally {
            setLoading(false);
        }
    };

    // ==================== DIALOG HANDLERS ====================

    const handleOpenTerminateDialog = () => {
        setTerminateForm({
            terminationReason: '',
            vacateDate: new Date().toISOString().split('T')[0],
            refundDeposit: false
        });
        setShowTerminateDialog(true);
    };

    const handleCloseTerminateDialog = () => {
        setShowTerminateDialog(false);
        setTerminateForm({
            terminationReason: '',
            vacateDate: '',
            refundDeposit: false
        });
    };

    const handleOpenSwitchRoomDialog = () => {
        setSwitchRoomForm({
            newPropertyId: lease.property_id || '',
            newHouseNumber: '',
            newMonthlyRent: lease.monthly_rent || '',
            switchDate: new Date().toISOString().split('T')[0],
            transferArrears: false,
            billingEnabled: true
        });
        setShowSwitchRoomDialog(true);
    };

    const handleCloseSwitchRoomDialog = () => {
        setShowSwitchRoomDialog(false);
        setSwitchRoomForm({
            newPropertyId: '',
            newHouseNumber: '',
            newMonthlyRent: '',
            switchDate: '',
            transferArrears: false,
            billingEnabled: true
        });
    };

    const handleOpenExtendDialog = () => {
        setExtendForm({
            newEndDate: '',
            newMonthlyRent: '',
            reason: ''
        });
        setShowExtendDialog(true);
    };

    const handleCloseExtendDialog = () => {
        setShowExtendDialog(false);
        setExtendForm({
            newEndDate: '',
            newMonthlyRent: '',
            reason: ''
        });
    };

    const handleOpenUpdateTermsDialog = () => {
        setUpdateTermsForm({
            monthlyRent: '',
            depositHeld: '',
            billingEnabled: null,
            waterUnits: '',
            electricityUnits: '',
            leaseStartDate: '',
            leaseEndDate: ''
        });
        setShowUpdateTermsDialog(true);
    };

    const handleCloseUpdateTermsDialog = () => {
        setShowUpdateTermsDialog(false);
        setUpdateTermsForm({
            monthlyRent: '',
            depositHeld: '',
            billingEnabled: null,
            waterUnits: '',
            electricityUnits: '',
            leaseStartDate: '',
            leaseEndDate: ''
        });
    };

    const handleOpenDepositDialog = () => {
        // Initialize with existing deposits
        const existingDeposits = Array.isArray(lease?.deposits) ? lease.deposits.map(dep => ({
            Description: dep.Description || dep.description || '',
            Amount: dep.Amount || dep.amount || '',
            Refundable: dep.Refundable || dep.refundable || 'YES'
        })) : [];

        setDepositForm(existingDeposits.length > 0 ? existingDeposits : [
            { Description: '', Amount: '', Refundable: 'YES' }
        ]);
        setShowDepositDialog(true);
    };

    const handleCloseDepositDialog = () => {
        setShowDepositDialog(false);
        setDepositForm([]);
    };

    const handleAddDeposit = () => {
        setDepositForm([...depositForm, { Description: '', Amount: '', Refundable: 'YES' }]);
    };

    const handleRemoveDeposit = (index) => {
        const newDeposits = depositForm.filter((_, i) => i !== index);
        setDepositForm(newDeposits);
    };

    const handleDepositChange = (index, field, value) => {
        const newDeposits = [...depositForm];
        newDeposits[index][field] = value;
        setDepositForm(newDeposits);
    };

    const handleSaveDeposits = async () => {
        // Validate deposits
        const validDeposits = depositForm.filter(dep =>
            dep.Description.trim() !== '' && dep.Amount !== '' && !isNaN(parseFloat(dep.Amount))
        );

        if (validDeposits.length === 0) {
            showSnackbar({
                title: "Validation Error!",
                message: "Please add at least one valid deposit entry.",
                type: "error",
                position: { vertical: "bottom", horizontal: "right" },
            });
            return;
        }

        try {
            setLoading(true);
            const response = await tenantsApi.updateDepositDetails({
                tenant_id: lease.tenant_id,
                deposits: validDeposits
            });

            if (response.ok || response.data?.success) {
                showSnackbar({
                    title: "Success!",
                    message: response.data?.message || "Deposits updated successfully!",
                    type: "success",
                    position: { vertical: "bottom", horizontal: "right" } ,
                });

                handleCloseDepositDialog();
                onLeaseUpdated?.();
            } else {
                showSnackbar({
                    title: "Error!",
                    message: response.data?.Error || "Failed to update deposits.",
                    type: "error",
                    position: { vertical: "bottom", horizontal: "right" },
                });
            }
        } catch (error) {
            console.error('Error updating deposits:', error) ;
            showSnackbar({
                title: "Error!",
                message: "Failed to update deposits. Please try again.",
                type: "error",
                position: { vertical: "bottom", horizontal: "right" },
            });
        } finally {
            setLoading(false);
        }
    };

    // ==================== RENDER ====================

    // Check both lease_status and state properties since different APIs may use different field names
    const isLeaseActive = lease?.lease_status === 'ACTIVE' || lease?.state === 'ACTIVE';

    // Debug logging
    console.log('LeaseManagementPanel - lease data:', {
        lease_status: lease?.lease_status,
        state: lease?.state,
        isLeaseActive,
        fullLease: lease
    });

    // Get deposits from lease data (API already returns parsed array)
    const deposits = Array.isArray(lease?.deposits) ? lease.deposits.map(dep => ({
        description: dep.Description || dep.description || '',
        amount: parseFloat(dep.Amount || dep.amount || 0),
        refundable: (dep.Refundable || dep.refundable) === 'YES'
    })) : [];

    const totalDeposit = deposits.reduce((sum, dep) => sum + dep.amount, 0);

    console.log('Deposits from API:', lease?.deposits);
    console.log('Formatted deposits:', deposits);
    console.log('Total deposit:', totalDeposit);

    return (
        <Box>
            {/* Deposit Details Card */}
            {deposits.length > 0 && (
                <StyledCard>
                    <CardContent>
                        <DepositCardHeader>
                            <DepositHeaderIcon>
                                <Shield size={24} color={themeColor} />
                                <Typography variant="h6" sx={{ fontWeight: 700, color: themeColor, m: 0 }}>
                                    Deposit Details
                                </Typography>
                            </DepositHeaderIcon>
                            <Button
                                size="small"
                                variant="outlined"
                                startIcon={<Edit size={16} />}
                                onClick={handleOpenDepositDialog}
                                sx={{
                                    textTransform: 'none',
                                    borderColor: themeColor,
                                    color: themeColor,
                                    '&:hover': {
                                        backgroundColor: `${themeColor}10`,
                                        borderColor: themeColor
                                    }
                                }}
                            >
                                Edit
                            </Button>
                        </DepositCardHeader>

                        <Box>
                            {deposits.map((deposit, index) => (
                                <DepositItemCard key={index} variant="outlined">
                                    <CardContent>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
                                            <Box sx={{ flex: 1 }}>
                                                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#333', mb: 0.5 }}>
                                                    {deposit.description}
                                                </Typography>
                                                <DepositAmountText>
                                                    KES {deposit.amount.toLocaleString()}
                                                </DepositAmountText>
                                            </Box>
                                            {deposit.refundable ? (
                                                <RefundableChip
                                                    icon={<RotateCcw size={16} />}
                                                    label="Refundable"
                                                    variant="outlined"
                                                    size="small"
                                                />
                                            ) : (
                                                <NonRefundableChip
                                                    icon={<Shield size={16} />}
                                                    label="Non-refundable"
                                                    variant="outlined"
                                                    size="small"
                                                />
                                            )}
                                        </Box>
                                    </CardContent>
                                </DepositItemCard>
                            ))}

                            <DepositSummaryBox>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#555' }}>
                                        Total Deposit Held
                                    </Typography>
                                    <Typography variant="h5" sx={{ fontWeight: 800, color: themeColor, letterSpacing: '0.5px' }}>
                                        KES {totalDeposit.toLocaleString()}
                                    </Typography>
                                </Box>
                            </DepositSummaryBox>
                        </Box>
                    </CardContent>
                </StyledCard>
            )}

            {/* Quick Actions */}
            <StyledCard>
                <CardContent>
                    <Typography variant="h6" sx={{ fontWeight: 600, color: themeColor, mb: 2 }}>
                        Lease Management
                    </Typography>

                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <Tooltip title={!isLeaseActive ? "Lease is not active" : ""}>
                                <span style={{ display: 'block' }}>
                                    <ActionButton
                                        fullWidth
                                        variant="outlined"
                                        startIcon={<ArrowRightLeft size={18} />}
                                        onClick={handleOpenSwitchRoomDialog}
                                        disabled={!isLeaseActive}
                                    >
                                        Switch Room
                                    </ActionButton>
                                </span>
                            </Tooltip>
                        </Grid>

                        {/* <Grid item xs={12} sm={6}>
                            <Tooltip title={!isLeaseActive ? "Lease is not active" : ""}>
                                <span style={{ display: 'block' }}>
                                    <ActionButton
                                        fullWidth
                                        variant="outlined"
                                        startIcon={<TrendingUp size={18} />}
                                        onClick={handleOpenExtendDialog}
                                        disabled={!isLeaseActive}
                                    >
                                        Extend Lease
                                    </ActionButton>
                                </span>
                            </Tooltip>
                        </Grid> */}

                        {/* <Grid item xs={12} sm={6}>
                            <Tooltip title={!isLeaseActive ? "Lease is not active" : ""}>
                                <span style={{ display: 'block' }}>
                                    <ActionButton
                                        fullWidth
                                        variant="outlined"
                                        startIcon={<Edit size={18} />}
                                        onClick={handleOpenUpdateTermsDialog}
                                        disabled={!isLeaseActive}
                                    >
                                        Update Terms
                                    </ActionButton>
                                </span>
                            </Tooltip>
                        </Grid> */}

                        <Grid item xs={12} sm={6}>
                            <ActionButton
                                fullWidth
                                variant="outlined"
                                startIcon={<FileText size={18} />}
                                onClick={loadLeaseHistory}
                            >
                                View History
                            </ActionButton>
                        </Grid>

                        <Grid item xs={12}>
                            <Tooltip title={!isLeaseActive ? "Lease is already terminated" : ""}>
                                <span style={{ display: 'block' }}>
                                    <ActionButton
                                        fullWidth
                                        variant="danger"
                                        startIcon={<LogOut size={18} />}
                                        onClick={handleOpenTerminateDialog}
                                        disabled={!isLeaseActive}
                                    >
                                        Terminate Lease
                                    </ActionButton>
                                </span>
                            </Tooltip>
                        </Grid>
                    </Grid>
                </CardContent>
            </StyledCard>


            {/* Terminate Lease Dialog */}
            <StyledDialog open={showTerminateDialog} onClose={handleCloseTerminateDialog}>
                <DialogTitleStyled>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <LogOut size={24} />
                        Terminate Lease
                    </Box>
                    <IconButton onClick={handleCloseTerminateDialog} size="small">
                        <CloseIcon size={20} />
                    </IconButton>
                </DialogTitleStyled>

                <DialogContent sx={{ pt: 3 }}>
                    <Alert severity="warning" sx={{ mb: 3 }}>
                        This action will terminate the lease and mark the unit as vacant. This cannot be undone.
                    </Alert>

                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Reason for Termination"
                                value={terminateForm.terminationReason}
                                onChange={(e) => setTerminateForm({ ...terminateForm, terminationReason: e.target.value })}
                                placeholder="e.g., End of contract, Eviction, Tenant request"
                                multiline
                                rows={2}
                                required
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Vacate Date"
                                type="date"
                                value={terminateForm.vacateDate}
                                onChange={(e) => setTerminateForm({ ...terminateForm, vacateDate: e.target.value })}
                                InputLabelProps={{ shrink: true }}
                                required
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <FormControlLabel
                                control={
                                    <MuiSwitch
                                        checked={terminateForm.refundDeposit}
                                        onChange={(e) => setTerminateForm({ ...terminateForm, refundDeposit: e.target.checked })}
                                    />
                                }
                                label="Process Deposit Refund"
                            />
                            <Typography variant="caption" color="textSecondary" sx={{ display: 'block', ml: 4 }}>
                                Outstanding balance will be deducted from deposit before refund
                            </Typography>
                        </Grid>

                        {leaseDetails && terminateForm.refundDeposit && (
                            <Grid item xs={12}>
                                <Alert severity="info" icon={<Info size={20} />}>
                                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                                        Refund Calculation:
                                    </Typography>
                                    <Box sx={{ fontSize: '0.875rem' }}>
                                        <div>Deposit Held: KES {parseFloat(leaseDetails.deposit_held || 0).toLocaleString()}</div>
                                        <div>Outstanding Balance: KES {((leaseDetails.total_arrears || 0) + (leaseDetails.total_pending_bills || 0)).toLocaleString()}</div>
                                        <Divider sx={{ my: 0.5 }} />
                                        <div style={{ fontWeight: 600 }}>
                                            Refund Amount: KES {Math.max(0, parseFloat(leaseDetails.deposit_held || 0) - ((leaseDetails.total_arrears || 0) + (leaseDetails.total_pending_bills || 0))).toLocaleString()}
                                        </div>
                                    </Box>
                                </Alert>
                            </Grid>
                        )}

                        {leaseDetails && leaseDetails.wallet_balance !== undefined && leaseDetails.wallet_balance !== 0 && (
                            <Grid item xs={12}>
                                {leaseDetails.status === "PAID" ? (
                                    <Alert severity="success" icon={<CheckCircle size={20} />}>
                                        <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                                            Tenant Overpayment (Refund Due)
                                        </Typography>
                                        <Typography variant="body2">
                                            Wallet Balance: <strong>KES {parseFloat(leaseDetails.wallet_balance || 0).toLocaleString()}</strong>
                                        </Typography>
                                        <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
                                            The tenant has overpaid. This amount is due as a refund upon lease termination.
                                        </Typography>
                                    </Alert>
                                ) : (
                                    <Alert severity="warning" icon={<AlertCircle size={20} />}>
                                        <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                                            Tenant Outstanding Balance
                                        </Typography>
                                        <Typography variant="body2">
                                            Amount Owing: <strong>KES {parseFloat(leaseDetails.wallet_balance || 0).toLocaleString()}</strong>
                                        </Typography>
                                        <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
                                            The tenant owes this amount. Consider collecting it before terminating the lease.
                                        </Typography>
                                    </Alert>
                                )}
                            </Grid>
                        )}
                    </Grid>
                </DialogContent>

                <DialogActions sx={{ p: 2.5, borderTop: '1px solid #e0e0e0' }}>
                    <Button onClick={handleCloseTerminateDialog} disabled={loading}>
                        Cancel
                    </Button>
                    <LoadingButton
                        loading={loading}
                        variant="contained"
                        color="error"
                        onClick={handleTerminateLease}
                    >
                        Terminate Lease
                    </LoadingButton>
                </DialogActions>
            </StyledDialog>

            {/* Switch Room Dialog */}
            <StyledDialog open={showSwitchRoomDialog} onClose={handleCloseSwitchRoomDialog}>
                <DialogTitleStyled>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <ArrowRightLeft size={24} />
                        Switch Room
                    </Box>
                    <IconButton onClick={handleCloseSwitchRoomDialog} size="small">
                        <CloseIcon size={20} />
                    </IconButton>
                </DialogTitleStyled>

                <DialogContent sx={{ pt: 3 }}>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <FormControl fullWidth required>
                                <InputLabel>Select Property</InputLabel>
                                <Select
                                    value={switchRoomForm.newPropertyId}
                                    label="Select Property"
                                    onChange={(e) => setSwitchRoomForm({ ...switchRoomForm, newPropertyId: e.target.value, newHouseNumber: '' })}
                                >
                                    {properties.map((property) => (
                                        <MenuItem key={property.property_id} value={property.property_id}>
                                            {property.property_name} - {property.location}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>

                        <Grid item xs={12}>
                            <FormControl fullWidth required disabled={!switchRoomForm.newPropertyId}>
                                <InputLabel>Select Unit</InputLabel>
                                <Select
                                    value={switchRoomForm.newHouseNumber}
                                    label="Select Unit"
                                    onChange={(e) => setSwitchRoomForm({ ...switchRoomForm, newHouseNumber: e.target.value })}
                                >
                                    {availableUnits.map((unit) => (
                                        <MenuItem key={unit.unit} value={unit.unit}>
                                            {unit.unit} ({unit.unit_type})
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>

                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Monthly Rent"
                                type="number"
                                value={switchRoomForm.newMonthlyRent}
                                onChange={(e) => setSwitchRoomForm({ ...switchRoomForm, newMonthlyRent: e.target.value })}
                                InputProps={{
                                    startAdornment: <InputAdornment position="start">KES</InputAdornment>
                                }}
                                required
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Switch Date"
                                type="date"
                                value={switchRoomForm.switchDate}
                                onChange={(e) => setSwitchRoomForm({ ...switchRoomForm, switchDate: e.target.value })}
                                InputLabelProps={{ shrink: true }}
                                required
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <FormControlLabel
                                control={
                                    <MuiSwitch
                                        checked={switchRoomForm.transferArrears}
                                        onChange={(e) => setSwitchRoomForm({ ...switchRoomForm, transferArrears: e.target.checked })}
                                    />
                                }
                                label="Transfer Unpaid Arrears to New Lease"
                            />
                        </Grid>
                    </Grid>
                </DialogContent>

                <DialogActions sx={{ p: 2.5, borderTop: '1px solid #e0e0e0' }}>
                    <Button onClick={handleCloseSwitchRoomDialog} disabled={loading}>
                        Cancel
                    </Button>
                    <LoadingButton
                        loading={loading}
                        variant="contained"
                        onClick={handleSwitchRoom}
                        sx={{ background: themeColor }}
                    >
                        Switch Room
                    </LoadingButton>
                </DialogActions>
            </StyledDialog>

            {/* Extend Lease Dialog */}
            <StyledDialog open={showExtendDialog} onClose={handleCloseExtendDialog}>
                <DialogTitleStyled>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <TrendingUp size={24} />
                        Extend Lease
                    </Box>
                    <IconButton onClick={handleCloseExtendDialog} size="small">
                        <CloseIcon size={20} />
                    </IconButton>
                </DialogTitleStyled>

                <DialogContent sx={{ pt: 3 }}>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <Alert severity="info" sx={{ mb: 2 }}>
                                Current end date: {lease?.lease_end_date ? new Date(lease.lease_end_date).toLocaleDateString() : 'N/A'}
                            </Alert>
                        </Grid>

                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="New End Date"
                                type="date"
                                value={extendForm.newEndDate}
                                onChange={(e) => setExtendForm({ ...extendForm, newEndDate: e.target.value })}
                                InputLabelProps={{ shrink: true }}
                                inputProps={{ min: lease?.lease_end_date?.split('T')[0] }}
                                required
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="New Monthly Rent (Optional)"
                                type="number"
                                value={extendForm.newMonthlyRent}
                                onChange={(e) => setExtendForm({ ...extendForm, newMonthlyRent: e.target.value })}
                                InputProps={{
                                    startAdornment: <InputAdornment position="start">KES</InputAdornment>
                                }}
                                helperText={`Current rent: KES ${lease?.monthly_rent?.toLocaleString() || '0'}`}
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Reason (Optional)"
                                value={extendForm.reason}
                                onChange={(e) => setExtendForm({ ...extendForm, reason: e.target.value })}
                                placeholder="e.g., Lease renewal - 1 year extension"
                                multiline
                                rows={2}
                            />
                        </Grid>
                    </Grid>
                </DialogContent>

                <DialogActions sx={{ p: 2.5, borderTop: '1px solid #e0e0e0' }}>
                    <Button onClick={handleCloseExtendDialog} disabled={loading}>
                        Cancel
                    </Button>
                    <LoadingButton
                        loading={loading}
                        variant="contained"
                        onClick={handleExtendLease}
                        sx={{ background: themeColor }}
                    >
                        Extend Lease
                    </LoadingButton>
                </DialogActions>
            </StyledDialog>

            {/* Update Terms Dialog */}
            <StyledDialog open={showUpdateTermsDialog} onClose={handleCloseUpdateTermsDialog} maxWidth="md">
                <DialogTitleStyled>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Edit size={24} />
                        Update Lease Terms
                    </Box>
                    <IconButton onClick={handleCloseUpdateTermsDialog} size="small">
                        <CloseIcon size={20} />
                    </IconButton>
                </DialogTitleStyled>

                <DialogContent sx={{ pt: 3 }}>
                    <Alert severity="info" sx={{ mb: 3 }}>
                        Update only the fields you want to change. Leave others empty.
                    </Alert>

                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Monthly Rent"
                                type="number"
                                value={updateTermsForm.monthlyRent}
                                onChange={(e) => setUpdateTermsForm({ ...updateTermsForm, monthlyRent: e.target.value })}
                                InputProps={{
                                    startAdornment: <InputAdornment position="start">KES</InputAdornment>
                                }}
                                helperText={`Current: KES ${parseFloat(lease?.monthly_rent || 0).toLocaleString()}`}
                            />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Deposit Amount"
                                type="number"
                                value={updateTermsForm.depositHeld}
                                onChange={(e) => setUpdateTermsForm({ ...updateTermsForm, depositHeld: e.target.value })}
                                InputProps={{
                                    startAdornment: <InputAdornment position="start">KES</InputAdornment>
                                }}
                                helperText={`Current: KES ${parseFloat(lease?.deposit_held || 0).toLocaleString()}`}
                            />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Water Reading"
                                type="number"
                                value={updateTermsForm.waterUnits}
                                onChange={(e) => setUpdateTermsForm({ ...updateTermsForm, waterUnits: e.target.value })}
                                helperText={`Current: ${lease?.current_water_reading || '0'} units`}
                            />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Electricity Reading"
                                type="number"
                                value={updateTermsForm.electricityUnits}
                                onChange={(e) => setUpdateTermsForm({ ...updateTermsForm, electricityUnits: e.target.value })}
                                helperText={`Current: ${lease?.current_electricity_reading || '0'} units`}
                            />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Lease Start Date"
                                type="date"
                                value={updateTermsForm.leaseStartDate}
                                onChange={(e) => setUpdateTermsForm({ ...updateTermsForm, leaseStartDate: e.target.value })}
                                InputLabelProps={{ shrink: true }}
                                helperText={`Current: ${lease?.lease_start_date ? new Date(lease.lease_start_date).toLocaleDateString() : 'N/A'}`}
                            />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Lease End Date"
                                type="date"
                                value={updateTermsForm.leaseEndDate}
                                onChange={(e) => setUpdateTermsForm({ ...updateTermsForm, leaseEndDate: e.target.value })}
                                InputLabelProps={{ shrink: true }}
                                helperText={`Current: ${lease?.lease_end_date ? new Date(lease.lease_end_date).toLocaleDateString() : 'N/A'}`}
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <FormControl fullWidth>
                                <InputLabel>Automatic Billing</InputLabel>
                                <Select
                                    value={updateTermsForm.billingEnabled === null ? '' : updateTermsForm.billingEnabled}
                                    label="Automatic Billing"
                                    onChange={(e) => setUpdateTermsForm({ ...updateTermsForm, billingEnabled: e.target.value === '' ? null : e.target.value })}
                                >
                                    <MenuItem value="">No Change</MenuItem>
                                    <MenuItem value={true}>Enabled</MenuItem>
                                    <MenuItem value={false}>Disabled</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                    </Grid>
                </DialogContent>

                <DialogActions sx={{ p: 2.5, borderTop: '1px solid #e0e0e0' }}>
                    <Button onClick={handleCloseUpdateTermsDialog} disabled={loading}>
                        Cancel
                    </Button>
                    <LoadingButton
                        loading={loading}
                        variant="contained"
                        onClick={handleUpdateTerms}
                        sx={{ background: themeColor }}
                    >
                        Update Terms
                    </LoadingButton>
                </DialogActions>
            </StyledDialog>

            {/* Lease History Dialog */}
            <StyledDialog
                open={showHistoryDialog}
                onClose={() => setShowHistoryDialog(false)}
                maxWidth="md"
                fullWidth
            >
                <DialogTitleStyled>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <FileText size={24} />
                        Lease History
                    </Box>
                    <IconButton onClick={() => setShowHistoryDialog(false)} size="small">
                        <CloseIcon size={20} />
                    </IconButton>
                </DialogTitleStyled>

                <DialogContent sx={{ pt: 3 }}>
                    {leaseHistory && (
                        <>
                            <Box sx={{ mb: 3 }}>
                                <Typography variant="h6" gutterBottom>
                                    {leaseHistory.tenant?.first_name} {leaseHistory.tenant?.last_name}
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                                    <Chip label={`Total Leases: ${leaseHistory.total_leases}`} color="primary" size="small" />
                                    <Chip label={`Active: ${leaseHistory.active_leases}`} color="success" size="small" />
                                    <Chip label={`Vacated: ${leaseHistory.vacated_leases}`} color="default" size="small" />
                                </Box>
                            </Box>

                            <TableContainer component={Paper} variant="outlined">
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell sx={{ fontWeight: 600 }}>Property</TableCell>
                                            <TableCell sx={{ fontWeight: 600 }}>Unit</TableCell>
                                            <TableCell sx={{ fontWeight: 600 }}>Duration</TableCell>
                                            <TableCell sx={{ fontWeight: 600 }}>Rent</TableCell>
                                            <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {leaseHistory.lease_history?.map((historyItem) => (
                                            <TableRow key={historyItem.lease_id}>
                                                <TableCell>
                                                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                        {historyItem.property_name}
                                                    </Typography>
                                                    <Typography variant="caption" color="textSecondary">
                                                        {historyItem.location}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>{historyItem.house_number}</TableCell>
                                                <TableCell>
                                                    <Typography variant="body2">
                                                        {historyItem.duration_months} months
                                                    </Typography>
                                                    <Typography variant="caption" color="textSecondary">
                                                        {new Date(historyItem.lease_start_date).toLocaleDateString()} - {new Date(historyItem.lease_end_date).toLocaleDateString()}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>KES {historyItem.monthly_rent?.toLocaleString()}</TableCell>
                                                <TableCell>
                                                    <Chip
                                                        label={historyItem.lease_status}
                                                        color={historyItem.lease_status === 'ACTIVE' ? 'success' : 'default'}
                                                        size="small"
                                                    />
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </>
                    )}
                </DialogContent>

                <DialogActions sx={{ p: 2.5, borderTop: '1px solid #e0e0e0' }}>
                    <Button onClick={() => setShowHistoryDialog(false)}>
                        Close
                    </Button>
                </DialogActions>
            </StyledDialog>

            {/* Deposit Management Dialog */}
            <StyledDialog open={showDepositDialog} onClose={handleCloseDepositDialog} maxWidth="md">
                <DialogTitleStyled>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <DollarSign size={24} />
                        Manage Deposit Details
                    </Box>
                    <IconButton onClick={handleCloseDepositDialog} size="small">
                        <CloseIcon size={20} />
                    </IconButton>
                </DialogTitleStyled>

                <DialogContent sx={{ pt: 3 }}>
                    <Alert severity="info" sx={{ mb: 3 }}>
                        Add, edit, or remove deposit entries. The total deposit amount will be automatically calculated.
                    </Alert>

                    <Box>
                        {depositForm.map((deposit, index) => (
                            <Card key={index} variant="outlined" sx={{ mb: 2, p: 2 }}>
                                <Grid container spacing={2} alignItems="center">
                                    <Grid item xs={12} sm={5}>
                                        <TextField
                                            fullWidth
                                            label="Description"
                                            value={deposit.Description}
                                            onChange={(e) => handleDepositChange(index, 'Description', e.target.value)}
                                            placeholder="e.g., Security Deposit"
                                            required
                                        />
                                    </Grid>

                                    <Grid item xs={12} sm={3}>
                                        <TextField
                                            fullWidth
                                            label="Amount"
                                            type="number"
                                            value={deposit.Amount}
                                            onChange={(e) => handleDepositChange(index, 'Amount', e.target.value)}
                                            InputProps={{
                                                startAdornment: <InputAdornment position="start">KES</InputAdornment>
                                            }}
                                            required
                                        />
                                    </Grid>

                                    <Grid item xs={12} sm={3}>
                                        <FormControl fullWidth>
                                            <InputLabel>Refundable</InputLabel>
                                            <Select
                                                value={deposit.Refundable}
                                                label="Refundable"
                                                onChange={(e) => handleDepositChange(index, 'Refundable', e.target.value)}
                                            >
                                                <MenuItem value="YES">Yes</MenuItem>
                                                <MenuItem value="NO">No</MenuItem>
                                            </Select>
                                        </FormControl>
                                    </Grid>

                                    <Grid item xs={12} sm={1}>
                                        <Tooltip title="Remove deposit">
                                            <IconButton
                                                color="error"
                                                onClick={() => handleRemoveDeposit(index)}
                                                disabled={depositForm.length === 1}
                                            >
                                                <Trash2 size={20} />
                                            </IconButton>
                                        </Tooltip>
                                    </Grid>
                                </Grid>
                            </Card>
                        ))}

                        <Button
                            fullWidth
                            variant="outlined"
                            startIcon={<Plus size={18} />}
                            onClick={handleAddDeposit}
                            sx={{ mt: 1, textTransform: 'none' }}
                        >
                            Add Another Deposit
                        </Button>

                        {/* Summary */}
                        {depositForm.length > 0 && (
                            <Box sx={{ mt: 3, p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
                                <Typography variant="subtitle2" gutterBottom>
                                    Summary
                                </Typography>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                    <Typography variant="body2">
                                        Refundable:
                                    </Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                        KES {depositForm
                                            .filter(d => d.Refundable === 'YES' && !isNaN(parseFloat(d.Amount)))
                                            .reduce((sum, d) => sum + parseFloat(d.Amount), 0)
                                            .toLocaleString()}
                                    </Typography>
                                </Box>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                    <Typography variant="body2">
                                        Non-Refundable:
                                    </Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                        KES {depositForm
                                            .filter(d => d.Refundable === 'NO' && !isNaN(parseFloat(d.Amount)))
                                            .reduce((sum, d) => sum + parseFloat(d.Amount), 0)
                                            .toLocaleString()}
                                    </Typography>
                                </Box>
                                <Divider sx={{ my: 1 }} />
                                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <Typography variant="body1" sx={{ fontWeight: 700 }}>
                                        Total Deposit:
                                    </Typography>
                                    <Typography variant="body1" sx={{ fontWeight: 700, color: themeColor }}>
                                        KES {depositForm
                                            .filter(d => !isNaN(parseFloat(d.Amount)))
                                            .reduce((sum, d) => sum + parseFloat(d.Amount), 0)
                                            .toLocaleString()}
                                    </Typography>
                                </Box>
                            </Box>
                        )}
                    </Box>
                </DialogContent>

                <DialogActions sx={{ p: 2.5, borderTop: '1px solid #e0e0e0' }}>
                    <Button onClick={handleCloseDepositDialog} disabled={loading}>
                        Cancel
                    </Button>
                    <LoadingButton
                        loading={loading}
                        variant="contained"
                        onClick={handleSaveDeposits}
                        sx={{ background: themeColor }}
                    >
                        Save Deposits
                    </LoadingButton>
                </DialogActions>
            </StyledDialog>
        </Box>
    );
};

export default LeaseManagementPanel;
